/** @format */
import {DetailedSensorCheck, SensorChecks, SensorGroupsList, SensorsList} from './types';
import {COLOR_TYPES} from './constants';

/**
 * Складываем два SensorChecks по правилам:
 * - простые поля (primary, new, newCount, fixed, fixedCount) суммируются
 * - detailed: при одинаковом id — суммируем value, иначе пушим новый
 */
const mergeSensorChecks = (prev: SensorChecks | undefined, cur: SensorChecks): SensorChecks => {
  if (!prev) {
    return {
      ...cur,
      detailed: cur.detailed.map(item => ({...item})),
    };
  }

  const detailedMap = new Map<string, DetailedSensorCheck>();

  prev.detailed.forEach(item => {
    detailedMap.set(item.id, {...item});
  });

  cur.detailed.forEach(item => {
    const existing = detailedMap.get(item.id);
    if (existing) {
      existing.value += item.value;
    } else {
      detailedMap.set(item.id, {...item});
    }
  });

  return {
    primary: prev.primary + cur.primary,
    new: prev.new + cur.new,
    newCount: prev.newCount + cur.newCount,
    fixed: prev.fixed + cur.fixed,
    fixedCount: prev.fixedCount + cur.fixedCount,
    detailed: Array.from(detailedMap.values()),
  };
};

/**
 * Складываем два SensorGroupsList: по каждому цвету RED/YELLOW/GRAY, если есть данные — мержим
 */
const mergeGroups = (prev: SensorGroupsList, cur: SensorGroupsList): SensorGroupsList => {
  const result: SensorGroupsList = {...prev};

  COLOR_TYPES.forEach(color => {
    const curChecks = cur[color];
    if (curChecks) {
      const prevChecks = result[color];
      result[color] = mergeSensorChecks(prevChecks, curChecks);
    }
  });

  return result;
};

/**
 * Основная функция.
 * 1. Идём по датам SensorsList в хронологическом порядке.
 * 2. Накапливаем данные в accumulator.
 * 3. Как только встречаем дату, которая входит в discreteDates, записываем текущее накопленное значение в result[date] и обнуляем accumulator.
 * В результате остаются только ключи-даты из discreteDates.
 */

const aggregateSensors = (sensors: SensorsList, discreteDates: string[]): SensorsList => {
  if (!discreteDates.length) {
    return {};
  }

  const result: SensorsList = {};

  const allDates = Object.keys(sensors).sort();
  const sortedDiscreteDates = [...discreteDates].sort();
  const discreteSet = new Set(sortedDiscreteDates);

  let accumulator: SensorGroupsList = {};

  allDates.forEach(date => {
    const dayData = sensors[date];

    if (dayData) {
      accumulator = mergeGroups(accumulator, dayData);
    }

    // Если текущая дата — контрольная, сохраняем накопленное и сбрасываем
    if (discreteSet.has(date)) {
      result[date] = accumulator;
      accumulator = {};
    }
  });

  if (Object.keys(accumulator).length) {
    const usedDates = Object.keys(result).sort();
    const lastDiscreteDate = sortedDiscreteDates[sortedDiscreteDates.length - 1];

    let targetDate: string;

    if (!usedDates.length) {
      // Ни одна дискретная дата ещё не была использована – сохраняем в последнюю
      targetDate = lastDiscreteDate;
    } else {
      const lastUsedDate = usedDates[usedDates.length - 1];
      // Ищем первую дискретную дату строго после последней использованной
      const nextDiscrete = sortedDiscreteDates.find(d => d > lastUsedDate);
      // Если такой нет – пишем в последнюю дискретную дату
      targetDate = nextDiscrete ?? lastDiscreteDate;
    }

    result[targetDate] = mergeGroups(result[targetDate] || {}, accumulator);
  }

  return result;
};

export {aggregateSensors};
